import type { DragEvent as ReactDragEvent } from 'react';
import { format, startOfDay, subMonths } from 'date-fns';

export type BrushRange = {
	startIndex: number;
	endIndex: number;
};

export type BrushDragEndPayload = {
	startIndex?: number;
	endIndex?: number;
};

export type BrushDragEndInput = ReactDragEvent<SVGElement> | BrushDragEndPayload;

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

export const getChartDateKey = (date: Date): string => {
	return format(startOfDay(date), 'yyyy-MM-dd');
};

export const formatChartDate = (dateValue: string): string => {
	const [year, month, day] = dateValue.split('-');

	if (!year || !month || !day) {
		return dateValue;
	}

	return `${day}/${month}/${year}`;
};

export const buildReadingDates = (dates: Date[]): Date[] => {
	const uniqueDates = new Map<string, Date>();

	dates
		.filter(isValidDate)
		.sort((a, b) => a.getTime() - b.getTime())
		.forEach((date) => {
			const normalizedDate = startOfDay(date);
			const dateKey = getChartDateKey(normalizedDate);

			if (!uniqueDates.has(dateKey)) {
				uniqueDates.set(dateKey, normalizedDate);
			}
		});

	return Array.from(uniqueDates.values());
};

export const buildReadingLookup = <T extends { data_leitura: string }>(leituras: T[]): Map<string, T> => {
	const lookup = new Map<string, T>();

	leituras.forEach((leitura) => {
		const date = new Date(leitura.data_leitura);

		if (!isValidDate(date)) {
			return;
		}

		const dateKey = getChartDateKey(date);

		if (!lookup.has(dateKey)) {
			lookup.set(dateKey, leitura);
		}
	});

	return lookup;
};

export const getBrushDragEndPayload = (event: BrushDragEndInput): BrushDragEndPayload => {
	if ('startIndex' in event || 'endIndex' in event) {
		return event;
	}

	return {};
};

export const getInitialBrushRange = (dates: Date[]): BrushRange => {
	const validDates = buildReadingDates(dates);

	if (validDates.length === 0) {
		return { startIndex: 0, endIndex: 0 };
	}

	const endIndex = validDates.length - 1;
	const initialDate = subMonths(validDates[endIndex], 6);
	const startIndex = validDates.findIndex((date) => date >= initialDate);

	return {
		startIndex: startIndex === -1 ? 0 : startIndex,
		endIndex,
	};
};

export const formatLimitValue = (value: number | null | undefined): string => {
	if (value == null || Number.isNaN(value)) {
		return '----';
	}

	return value.toLocaleString('pt-BR', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};
